import type {
	ConversationSummary,
	MembershipSummary,
	VisitorPage,
	VisitorStatus,
	VisitorSummary,
} from '@livechat/shared';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';

import {
	currentAccessToken,
	listVisitors,
	onAccessTokenChanged,
	realtimeUrl,
	refreshSession,
	startVisitorConversation,
} from '../api.js';
import { createRefreshRunner } from '../refresh-runner.js';
import { useDateFormat } from '../date-format.js';
import { visitorCompanyLabel } from '../visitor-company.js';
import { openCobrowseWindow } from '../cobrowse-window.js';
import { CountryFlag } from './VisitorLocation.js';
import { DateRangePicker, presetDateRange } from './DateRangePicker.js';
import { VisitorActionsMenu } from './VisitorActionsMenu.js';

const PAGE_SIZE = 50;

type VisitorSort = 'visitor' | 'device' | 'location' | 'page' | 'time-online' | 'visits' | 'chats';
type SortDirection = 'asc' | 'desc';

function visitorName(visitor: VisitorSummary): string {
	return visitor.name ?? visitor.email ?? visitor.id.slice(0, 8).toUpperCase();
}

function clientLabel(name?: string, version?: string): string {
	if (!name) return 'Unknown';
	return version ? `${name} ${version}` : name;
}

function elapsedDuration(value?: string, until?: string): string {
	if (!value) return '—';
	const since = new Date(value).getTime();
	const end = until ? new Date(until).getTime() : Date.now();
	if (!Number.isFinite(since) || !Number.isFinite(end)) return '—';
	const minutes = Math.max(0, Math.floor((end - since) / 60_000));
	if (minutes < 60) return `${minutes} min`;
	return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
}

function visitorCityText(visitor: VisitorSummary): string {
	return visitor.cityName ?? 'Unknown city';
}

function emptyVisitorPage(): VisitorPage {
	return {
		geoIpAvailable: false,
		items: [],
		page: 1,
		pageSize: PAGE_SIZE,
		total: 0,
	};
}

function VisitorSortHeading({
	activeSort,
	label,
	onSort,
	order,
	sort,
}: {
	activeSort: VisitorSort;
	label: string;
	onSort: (sort: VisitorSort) => void;
	order: SortDirection;
	sort: VisitorSort;
}) {
	const active = activeSort === sort;
	return (
		<span
			aria-sort={active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'}
			className="visitor-sort-heading"
			role="columnheader"
		>
			<button onClick={() => onSort(sort)} type="button">
				<span>{label}</span>
				{active && (
					<span aria-hidden="true" className={`visitor-sort-indicator ${order}`} />
				)}
			</button>
		</span>
	);
}

function VisitorGroup({
	activeOrder,
	activeSort,
	loading,
	onActiveSort,
	onPageChange,
	onCobrowse,
	onSelect,
	result,
	status,
}: {
	activeOrder: SortDirection;
	activeSort: VisitorSort;
	loading: boolean;
	onActiveSort: (sort: VisitorSort) => void;
	onPageChange: (page: number) => void;
	onCobrowse: (visitor: VisitorSummary) => void;
	onSelect: (visitor: VisitorSummary) => void;
	result: VisitorPage;
	status: VisitorStatus;
}) {
	const { formatDateTime } = useDateFormat();
	const label =
		status === 'in-chat' ? 'In Chat' : status === 'active' ? 'Active' : 'Past Visitors';
	const pageCount = Math.max(1, Math.ceil(result.total / result.pageSize));
	const first = result.total ? (result.page - 1) * result.pageSize + 1 : 0;
	const last = Math.min(result.total, result.page * result.pageSize);

	return (
		<section className="visitor-group" aria-labelledby={`visitor-group-${status}`}>
			<header className="table-section-heading">
				<div>
					<span className={`visitor-status-dot ${status}`} />
					<h3 id={`visitor-group-${status}`}>{label}</h3>
				</div>
				<span className="visitor-group-count table-section-count">{result.total}</span>
			</header>
			<div className="table-card paginated-table visitor-table-card">
				<div
					className="visitor-table paginated-table-scroll"
					role="table"
					aria-busy={loading}
				>
					<div
						className={`visitor-row visitor-row-${status} visitor-head standard-table-head`}
						role="row"
					>
						{status === 'active' ? (
							<VisitorSortHeading
								activeSort={activeSort}
								label="Visitor"
								onSort={onActiveSort}
								order={activeOrder}
								sort="visitor"
							/>
						) : (
							<span>Visitor</span>
						)}
						{status === 'in-chat' && <span>Agent</span>}
						{status === 'active' ? (
							<>
								<VisitorSortHeading
									activeSort={activeSort}
									label="Device"
									onSort={onActiveSort}
									order={activeOrder}
									sort="device"
								/>
								<VisitorSortHeading
									activeSort={activeSort}
									label="Location"
									onSort={onActiveSort}
									order={activeOrder}
									sort="location"
								/>
								<VisitorSortHeading
									activeSort={activeSort}
									label="Page"
									onSort={onActiveSort}
									order={activeOrder}
									sort="page"
								/>
								<VisitorSortHeading
									activeSort={activeSort}
									label="Time Online"
									onSort={onActiveSort}
									order={activeOrder}
									sort="time-online"
								/>
								<VisitorSortHeading
									activeSort={activeSort}
									label="Visits"
									onSort={onActiveSort}
									order={activeOrder}
									sort="visits"
								/>
								<VisitorSortHeading
									activeSort={activeSort}
									label="Chats"
									onSort={onActiveSort}
									order={activeOrder}
									sort="chats"
								/>
							</>
						) : (
							<>
								<span>Device</span>
								<span>Location</span>
								<span>Page</span>
								{status === 'offline' && <span>Start Time</span>}
								<span>{status === 'in-chat' ? 'Time in chat' : 'Time Online'}</span>
								<span>Visits</span>
								<span>Chats</span>
							</>
						)}
						<span aria-label="Visitor actions" role="columnheader" />
					</div>
					{result.items.map((visitor) => (
						<div
							className={`visitor-row visitor-row-${status}`}
							key={visitor.id}
							role="row"
						>
							<span className="visitor-name-copy">
								<span className="visitor-name">{visitorName(visitor)}</span>
								{visitorCompanyLabel(visitor) && (
									<small>{visitorCompanyLabel(visitor)}</small>
								)}
							</span>
							{status === 'in-chat' && (
								<span className="visitor-agent">
									<span>{visitor.assignedUserName ?? '—'}</span>
									{visitor.assignedUserName && (
										<small>
											{visitor.assignedMemberType === 'ai_agent'
												? 'AI Agent'
												: 'Agent'}
										</small>
									)}
								</span>
							)}
							<span className="visitor-device" title={visitor.userAgent}>
								<span>
									{clientLabel(visitor.browserName, visitor.browserVersion)}
								</span>
								<small>
									{clientLabel(
										visitor.operatingSystemName,
										visitor.operatingSystemVersion,
									)}
								</small>
							</span>
							<span className="visitor-location">
								<span
									aria-label={visitor.countryName ?? 'Country unavailable'}
									className="visitor-country-flag"
									role="img"
									title={visitor.countryName ?? 'Country unavailable'}
								>
									<CountryFlag countryCode={visitor.countryCode} />
								</span>
								<span className="visitor-location-copy">
									<span className="visitor-ip">{visitor.ipAddress ?? '—'}</span>
									<small>{visitorCityText(visitor)}</small>
								</span>
							</span>
							<span className="visitor-page" title={visitor.pageUrl}>
								{visitor.pageTitle ??
									visitor.pageUrl ??
									visitor.originHost ??
									'Unknown page'}
							</span>
							{status === 'offline' && (
								<span>
									{visitor.activeSince
										? formatDateTime(visitor.activeSince)
										: '—'}
								</span>
							)}
							<span>
								{elapsedDuration(
									visitor.status === 'in-chat'
										? visitor.currentConversationStartedAt
										: visitor.activeSince,
									visitor.status === 'offline' ? visitor.lastSeenAt : undefined,
								)}
							</span>
							<span>{visitor.visitCount}</span>
							<span>{visitor.chatCount}</span>
							<VisitorActionsMenu
								visitor={visitor}
								name={visitorName(visitor)}
								onCobrowse={() => onCobrowse(visitor)}
								onStartChat={() => onSelect(visitor)}
							/>
						</div>
					))}
				</div>
				<footer
					aria-label={`${label} pagination`}
					className="data-pagination visitor-table-pagination"
				>
					<span>
						{first}–{last} of {result.total}
					</span>
					<div>
						<button
							aria-label={`Previous ${label.toLowerCase()} page`}
							disabled={result.page <= 1 || loading}
							onClick={() => onPageChange(Math.max(1, result.page - 1))}
							type="button"
						>
							‹
						</button>
						<span aria-live="polite">
							Page {result.page} of {pageCount}
						</span>
						<button
							aria-label={`Next ${label.toLowerCase()} page`}
							disabled={result.page >= pageCount || loading}
							onClick={() => onPageChange(Math.min(pageCount, result.page + 1))}
							type="button"
						>
							›
						</button>
					</div>
				</footer>
			</div>
		</section>
	);
}

export function TenantVisitors({
	membership,
	onOpenConversation,
}: {
	membership: MembershipSummary;
	onOpenConversation: (conversationId: string) => void;
}) {
	return (
		<TenantVisitorDirectory
			membership={membership}
			onOpenConversation={onOpenConversation}
			view="live"
		/>
	);
}

export function TenantVisitorHistory({
	membership,
	onOpenConversation,
}: {
	membership: MembershipSummary;
	onOpenConversation: (conversationId: string) => void;
}) {
	return (
		<TenantVisitorDirectory
			membership={membership}
			onOpenConversation={onOpenConversation}
			view="history"
		/>
	);
}

function TenantVisitorDirectory({
	membership,
	onOpenConversation,
	view,
}: {
	membership: MembershipSummary;
	onOpenConversation: (conversationId: string) => void;
	view: 'history' | 'live';
}) {
	const [draftQuery, setDraftQuery] = useState('');
	const [query, setQuery] = useState('');
	const [dateRange, setDateRange] = useState(() => presetDateRange());
	const [activeSort, setActiveSort] = useState<VisitorSort>('time-online');
	const [activeOrder, setActiveOrder] = useState<SortDirection>('asc');
	const [pages, setPages] = useState<Record<VisitorStatus, number>>({
		active: 1,
		'in-chat': 1,
		offline: 1,
	});
	const [results, setResults] = useState<Record<VisitorStatus, VisitorPage>>({
		active: emptyVisitorPage(),
		'in-chat': emptyVisitorPage(),
		offline: emptyVisitorPage(),
	});
	const [geoIpAvailable, setGeoIpAvailable] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();
	const [visitorToStart, setVisitorToStart] = useState<VisitorSummary>();
	const [starting, setStarting] = useState(false);
	const refreshRunner = useRef(createRefreshRunner()).current;

	const load = useCallback(
		() =>
			refreshRunner.run(async () => {
				setLoading(true);
				setError(undefined);
				try {
					const requestedStatuses: VisitorStatus[] =
						view === 'live' ? ['in-chat', 'active'] : ['offline'];
					const loaded = await Promise.all(
						requestedStatuses.map((requestedStatus) =>
							listVisitors(membership.tenantId, {
								...(view === 'history'
									? { from: dateRange.from, to: dateRange.to }
									: {}),
								...(requestedStatus === 'active'
									? { order: activeOrder, sort: activeSort }
									: {}),
								page: pages[requestedStatus],
								pageSize: PAGE_SIZE,
								query: query || undefined,
								status: requestedStatus,
							}),
						),
					);
					setResults((current) => {
						const next = { ...current };
						for (const [index, requestedStatus] of requestedStatuses.entries()) {
							next[requestedStatus] = loaded[index]!;
						}
						return next;
					});
					setGeoIpAvailable(loaded[0]?.geoIpAvailable ?? false);
				} catch (caught) {
					setError(caught instanceof Error ? caught.message : 'Could not load visitors');
				} finally {
					setLoading(false);
				}
			}),
		[
			activeOrder,
			activeSort,
			dateRange,
			membership.tenantId,
			pages,
			query,
			refreshRunner,
			view,
		],
	);

	useEffect(() => void load(), [load]);

	useEffect(() => {
		if (view === 'history') return;
		const token = currentAccessToken();
		if (!token) return;
		const socket = io(`${realtimeUrl}/chat`, {
			auth: { accessToken: token, tenantId: membership.tenantId },
			transports: ['websocket', 'polling'],
		});
		let refreshTimer: number | undefined;
		let lastRealtimeRefreshAt = 0;
		const refresh = () => {
			window.clearTimeout(refreshTimer);
			refreshTimer = window.setTimeout(() => void load(), 150);
		};
		const stopWatchingToken = onAccessTokenChanged((updatedToken) => {
			socket.auth = { accessToken: updatedToken, tenantId: membership.tenantId };
			if (updatedToken && !socket.connected) socket.connect();
		});
		socket.on('visitor:presence', refresh);
		socket.on('visitor:updated', refresh);
		socket.on('conversation:created', refresh);
		socket.on('conversation:updated', refresh);
		socket.on('session:error', () => {
			if (Date.now() - lastRealtimeRefreshAt < 60_000) return;
			lastRealtimeRefreshAt = Date.now();
			void refreshSession()
				.then((authentication) => {
					socket.auth = {
						accessToken: authentication.accessToken,
						tenantId: membership.tenantId,
					};
					socket.connect();
				})
				.catch((caught: unknown) =>
					setError(
						caught instanceof Error ? caught.message : 'Realtime connection failed',
					),
				);
		});
		const interval = window.setInterval(() => void load(), 20_000);
		return () => {
			window.clearInterval(interval);
			window.clearTimeout(refreshTimer);
			stopWatchingToken();
			socket.disconnect();
		};
	}, [load, membership.tenantId, view]);

	const visibleStatuses: VisitorStatus[] = view === 'live' ? ['in-chat', 'active'] : ['offline'];
	const visibleTotal = visibleStatuses.reduce(
		(total, visibleStatus) => total + results[visibleStatus].total,
		0,
	);

	function search(event: FormEvent<HTMLFormElement>): void {
		event.preventDefault();
		setPages({ active: 1, 'in-chat': 1, offline: 1 });
		setQuery(draftQuery.trim());
	}

	function setGroupPage(groupStatus: VisitorStatus, page: number): void {
		setPages((current) => ({ ...current, [groupStatus]: page }));
	}

	function sortActiveVisitors(sort: VisitorSort): void {
		setPages((current) => ({ ...current, active: 1 }));
		if (sort === activeSort) {
			setActiveOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
			return;
		}
		setActiveSort(sort);
		setActiveOrder('asc');
	}

	function selectVisitor(visitor: VisitorSummary): void {
		if (visitor.currentConversationId) {
			onOpenConversation(visitor.currentConversationId);
			return;
		}
		if (visitor.status === 'active') setVisitorToStart(visitor);
	}

	function cobrowse(visitor: VisitorSummary): void {
		try {
			openCobrowseWindow(membership.tenantId, visitor);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not open Cobrowse');
		}
	}

	async function startChat(): Promise<void> {
		if (!visitorToStart) return;
		setStarting(true);
		setError(undefined);
		try {
			const conversation: ConversationSummary = await startVisitorConversation(
				membership.tenantId,
				visitorToStart.id,
			);
			setVisitorToStart(undefined);
			onOpenConversation(conversation.id);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not start the chat');
		} finally {
			setStarting(false);
		}
	}

	return (
		<section className="data-page visitors-page" aria-labelledby={`${view}-visitors-heading`}>
			<header className="visitors-toolbar">
				<h2 id={`${view}-visitors-heading`}>
					{view === 'live' ? 'Visitors' : 'Visitor History'}
				</h2>
				<div className="visitor-filters">
					{view === 'history' && (
						<DateRangePicker
							includeTime={false}
							onChange={(range) => {
								setPages((current) => ({ ...current, offline: 1 }));
								setDateRange(range);
							}}
							value={dateRange}
						/>
					)}
					<form className="data-search" onSubmit={search} role="search">
						<label className="visually-hidden" htmlFor={`${view}-visitor-search`}>
							Search visitors
						</label>
						<input
							id={`${view}-visitor-search`}
							onChange={(event) => setDraftQuery(event.currentTarget.value)}
							placeholder={
								view === 'history' ? 'Search visitor history…' : 'Search visitors…'
							}
							value={draftQuery}
						/>
						<button className="secondary-button compact-button">Search</button>
					</form>
				</div>
			</header>

			{error && <div className="alert error data-page-alert">{error}</div>}
			{!loading && !geoIpAvailable && (
				<div className="alert warning data-page-alert">
					Visitor locations are temporarily unavailable. An owner should check the GeoIP
					database on the server.
				</div>
			)}
			<div className="visitor-groups" aria-busy={loading}>
				{visibleStatuses.map(
					(visibleStatus) =>
						results[visibleStatus].total > 0 && (
							<VisitorGroup
								activeOrder={activeOrder}
								activeSort={activeSort}
								key={visibleStatus}
								loading={loading}
								onActiveSort={sortActiveVisitors}
								onCobrowse={cobrowse}
								onPageChange={(page) => setGroupPage(visibleStatus, page)}
								onSelect={selectVisitor}
								result={results[visibleStatus]}
								status={visibleStatus}
							/>
						),
				)}
				{!loading && visibleTotal === 0 && (
					<div className="data-page-panel data-empty">
						<strong>No visitors found</strong>
						<span>Visitor activity will appear here in real time.</span>
					</div>
				)}
				{loading && visibleTotal === 0 && (
					<div className="data-page-panel data-empty">Loading visitors…</div>
				)}
			</div>

			{visitorToStart && (
				<div className="dialog-backdrop" role="presentation">
					<section
						aria-labelledby="start-chat-heading"
						aria-modal="true"
						className="dialog-card"
						role="dialog"
					>
						<h3 id="start-chat-heading">Start a chat?</h3>
						<p>
							Invite <strong>{visitorName(visitorToStart)}</strong> to chat while they
							are viewing{' '}
							{visitorToStart.pageTitle ??
								visitorToStart.originHost ??
								'the current page'}
							.
						</p>
						<div className="dialog-actions">
							<button
								className="secondary-button"
								disabled={starting}
								onClick={() => setVisitorToStart(undefined)}
							>
								Cancel
							</button>
							<button
								className="primary-button"
								disabled={starting}
								onClick={() => void startChat()}
							>
								{starting ? 'Starting…' : 'Start chat'}
							</button>
						</div>
					</section>
				</div>
			)}
		</section>
	);
}
